import { useState, useEffect } from 'react'
import { Table, Card, Button, Tag, Space, Modal, Form, InputNumber, Input, Select, message, DatePicker } from 'antd'
import { PlusOutlined, CheckOutlined } from '@ant-design/icons'
import { monthlyBillingApi } from '../services/api'
import api from '../services/api'

const statusMap: Record<number, { label: string; color: string }> = {
  0: { label: '未申请', color: 'default' },
  1: { label: '申请中', color: 'orange' },
  2: { label: '已开通', color: 'green' },
  3: { label: '已拒绝', color: 'red' }
}

const billingStatusMap: Record<number, { label: string; color: string }> = {
  1: { label: '待确认', color: 'orange' },
  2: { label: '待支付', color: 'blue' },
  3: { label: '已支付', color: 'green' },
  4: { label: '已作废', color: 'default' }
}

export default function MonthlyBilling() {
  const [loading, setLoading] = useState(false)
  const [customers, setCustomers] = useState<any[]>([])
  const [billings, setBillings] = useState<any[]>([])
  const [auditModalOpen, setAuditModalOpen] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null)
  const [auditLoading, setAuditLoading] = useState(false)
  const [generateModalOpen, setGenerateModalOpen] = useState(false)
  const [generateForm] = Form.useForm()
  const [generateLoading, setGenerateLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('customers')
  const [enterpriseOptions, setEnterpriseOptions] = useState<any[]>([])

  useEffect(() => {
    if (activeTab === 'customers') loadCustomers()
    else loadBillings()
  }, [activeTab])

  // 加载已开通月结的企业列表（用于生成账单时选择）
  const loadEnterpriseOptions = async () => {
    try {
      const res: any = await api.get('/enterprise-clients', { params: { page_size: 100, status: 1 } })
      const list = res.data?.list || []
      // 只显示已开通月结的（monthly_status=2）
      setEnterpriseOptions(list.filter((c: any) => c.monthly_status === 2))
    } catch {
      // 静默失败
    }
  }

  const loadCustomers = async () => {
    setLoading(true)
    try {
      const res: any = await monthlyBillingApi.getCustomers({ params: {} })
      const all: any[] = res.data?.list || []
      setCustomers(all)
    } catch {
      message.error('加载失败')
    } finally {
      setLoading(false)
    }
  }

  const loadBillings = async () => {
    setLoading(true)
    try {
      const res: any = await monthlyBillingApi.getBillings({})
      setBillings(res.data?.list || [])
    } catch {
      message.error('加载失败')
    } finally {
      setLoading(false)
    }
  }

  const handleAudit = (record: any) => {
    setSelectedCustomer(record)
    setAuditModalOpen(true)
  }

  const submitAudit = async (values: any) => {
    if (!selectedCustomer) return
    setAuditLoading(true)
    try {
      await monthlyBillingApi.audit(selectedCustomer.id, {
        status: values.status,
        remark: values.remark,
        credit_limit: values.credit_limit
      })
      message.success(values.status === 2 ? '月结已开通' : '月结申请已拒绝')
      setAuditModalOpen(false)
      loadCustomers()
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.data?.message || '操作失败')
    } finally {
      setAuditLoading(false)
    }
  }

  const openGenerateModal = () => {
    loadEnterpriseOptions()
    setGenerateModalOpen(true)
  }

  const handleGenerate = async () => {
    try {
      const values = await generateForm.validateFields()
      setGenerateLoading(true)
      const res: any = await monthlyBillingApi.generate({
        billing_month: values.billing_month.format('YYYY-MM'),
        enterprise_client_id: values.enterprise_client_id || undefined
      })
      message.success(res.message || '账单生成成功')
      setGenerateModalOpen(false)
      generateForm.resetFields()
      loadBillings()
    } catch (err: any) {
      if (!err.message?.includes('validate')) {
        message.error(err?.response?.data?.message || err?.data?.message || '生成失败')
      }
    } finally {
      setGenerateLoading(false)
    }
  }

  const columns = [
    { title: '企业名称', dataIndex: 'company_name', key: 'company_name' },
    { title: '联系人', dataIndex: 'contact_name', key: 'contact_name' },
    { title: '联系电话', dataIndex: 'contact_phone', key: 'contact_phone' },
    {
      title: '月结状态',
      dataIndex: 'monthly_status',
      key: 'monthly_status',
      render: (v: number) => {
        const s = statusMap[v] || statusMap[0]
        return <Tag color={s.color}>{s.label}</Tag>
      }
    },
    { title: '月结额度', dataIndex: 'monthly_credit_limit', key: 'monthly_credit_limit', render: (v: number) => `¥${Number(v || 0).toFixed(2)}` },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: any) => (
        record.monthly_status === 1 ? (
          <Space>
            <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => handleAudit(record)}>审核</Button>
          </Space>
        ) : record.monthly_status === 2 ? (
          <Tag color="green">已开通</Tag>
        ) : record.monthly_status === 3 ? (
          <Space>
            <Tag color="red">已拒绝</Tag>
            <Button size="small" onClick={() => handleAudit({ ...record, monthly_status: 1 })}>重新审核</Button>
          </Space>
        ) : <span style={{ color: '#aaa' }}>-</span>
      )
    }
  ]

  const billingColumns = [
    { title: '账单编号', dataIndex: 'billing_no', key: 'billing_no' },
    { title: '企业名称', dataIndex: 'company_name', key: 'company_name' },
    { title: '手机号', dataIndex: 'user_phone', key: 'user_phone' },
    { title: '账单月份', dataIndex: 'billing_month', key: 'billing_month' },
    { title: '订单数', dataIndex: 'order_count', key: 'order_count' },
    { title: '账单金额', dataIndex: 'total_amount', key: 'total_amount', render: (v: number) => `¥${Number(v || 0).toFixed(2)}` },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (v: number) => {
        const s = billingStatusMap[v] || billingStatusMap[1]
        return <Tag color={s.color}>{s.label}</Tag>
      }
    }
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>月结管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={openGenerateModal}>
          生成账单
        </Button>
      </div>

      <Card>
        <div style={{ marginBottom: 16 }}>
          <Button type={activeTab === 'customers' ? 'primary' : 'default'} onClick={() => setActiveTab('customers')}>客户审核</Button>
          <Button type={activeTab === 'billings' ? 'primary' : 'default'} onClick={() => setActiveTab('billings')} style={{ marginLeft: 8 }}>账单管理</Button>
        </div>

        {activeTab === 'customers' ? (
          <Table
            dataSource={customers}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
          />
        ) : (
          <Table
            dataSource={billings}
            columns={billingColumns}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
          />
        )}
      </Card>

      {/* 审核弹窗 */}
      <Modal
        title="月结审核"
        open={auditModalOpen}
        onCancel={() => setAuditModalOpen(false)}
        footer={null}
      >
        {selectedCustomer && (
          <Form onFinish={submitAudit} layout="vertical" style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 16 }}>
              <strong>企业名称：</strong>{selectedCustomer.company_name}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>联系人：</strong>{selectedCustomer.contact_name} &nbsp; {selectedCustomer.contact_phone}
            </div>
            <Form.Item name="status" label="审核结果" rules={[{ required: true }]} initialValue={2}>
              <Select placeholder="请选择审核结果">
                <Select.Option value={2}>✅ 开通月结</Select.Option>
                <Select.Option value={3}>❌ 拒绝申请</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="credit_limit" label="月结额度" initialValue={selectedCustomer.monthly_credit_limit}>
              <InputNumber min={0} style={{ width: '100%' }} placeholder="请输入额度（元）" />
            </Form.Item>
            <Form.Item name="remark" label="备注">
              <Input.TextArea rows={3} placeholder="可选，填写处理备注" />
            </Form.Item>
            <Button type="primary" htmlType="submit" block loading={auditLoading}>确认提交</Button>
          </Form>
        )}
      </Modal>

      {/* 生成账单弹窗 */}
      <Modal
        title="生成月结账单"
        open={generateModalOpen}
        onOk={handleGenerate}
        onCancel={() => setGenerateModalOpen(false)}
        confirmLoading={generateLoading}
        okText="生成"
      >
        <Form form={generateForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="billing_month" label="账单月份" rules={[{ required: true, message: '请选择账单月份' }]}>
            <DatePicker picker="month" style={{ width: '100%' }} placeholder="选择月份" />
          </Form.Item>
          <Form.Item name="enterprise_client_id" label="选择企业（不选则为所有已开通企业生成）">
            <Select
              placeholder="可选择指定企业"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {enterpriseOptions.map((c: any) => (
                <Select.Option key={c.id} value={c.id}>
                  {c.company_name}（额度：¥{Number(c.credit_limit || 0).toFixed(2)}）
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          {enterpriseOptions.length === 0 && (
            <div style={{ color: '#faad14', marginBottom: 16 }}>
              ⚠️ 当前没有已开通月结的企业客户。请先在企业客户管理中新增企业并审核通过月结申请。
            </div>
          )}
        </Form>
      </Modal>
    </div>
  )
}
